import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import * as Sentry from '@sentry/react';
import { api } from '../api/http';
import type { AuthConfig } from './msalConfig';
import { stashPushRedirectForAuth } from '../utils/swNavigationBridge';
import { usePushSubscription } from '../hooks/usePushSubscription';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  authEnabled: boolean;
  user: AccountInfo | null;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  authMethods: string[];
  markNativeAuthenticated: () => void;
  markNativeUnauthenticated: () => void;
  activeAuthMethod: 'microsoft' | 'native' | null;
  nativeRemember: boolean;
  idleMinutes: number;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  authEnabled: false,
  user: null,
  login: () => {},
  logout: () => {},
  getAccessToken: async () => null,
  authMethods: [],
  markNativeAuthenticated: () => {},
  markNativeUnauthenticated: () => {},
  activeAuthMethod: null,
  nativeRemember: false,
  idleMinutes: 60,
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

const LOGIN_SCOPES = ['openid', 'profile', 'email'];

// Refresh tokens 5 minutes before they expire
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/** Decode JWT payload without validation (for reading exp claim client-side). */
function parseJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

/**
 * P3-A1: mint the bridge cookie right after MSAL resolves a cached account.
 *
 * PP9: takes `getAccessToken` directly (AuthProvider's own closure) instead
 * of routing through the shared `api` client's module-level token-getter
 * (wired up later, in App.tsx's own effect). That avoids a race entirely —
 * this call needs no ordering guarantee against App.tsx's `setTokenGetter`
 * effect because it never depends on it.
 */
async function bridgeSessionAfterMicrosoftLogin(
  getAccessToken: () => Promise<string | null>,
): Promise<void> {
  try {
    const token = await getAccessToken();
    if (!token) return;
    const { isStandaloneDisplayMode } = await import('../utils/displayMode');
    await api.post(
      '/auth/session/bridge',
      { display_mode: isStandaloneDisplayMode() ? 'standalone' : 'browser' },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch {
    // Best-effort — a failed bridge call just means the NEXT app-open falls
    // back to MSAL's normal (shorter) silent-refresh path. Never blocks render.
  }
}

interface AuthProviderProps {
  msalInstance: PublicClientApplication | null;
  config: AuthConfig;
  children: ReactNode;
}

export function AuthProvider({ msalInstance, config, children }: AuthProviderProps) {
  const microsoftActive = !!msalInstance;
  const authMethods: string[] = config.methods ?? (config.enabled ? ['microsoft'] : []);
  const nativeActive = authMethods.includes('native');

  // null = still probing, true = authenticated, false = not authenticated
  const [nativeAuthed, setNativeAuthed] = useState<boolean | null>(nativeActive ? null : false);
  const [isLoading, setIsLoading] = useState(microsoftActive || nativeActive);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [activeAuthMethod, setActiveAuthMethod] = useState<'microsoft' | 'native' | null>(null);
  const [nativeRemember, setNativeRemember] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { unsubscribe: unsubscribePush } = usePushSubscription();

  // On mount, check if we already have a cached account (Microsoft) or probe native session
  useEffect(() => {
    if (microsoftActive) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        setUser(accounts[0]);
        setActiveAuthMethod('microsoft');
        void bridgeSessionAfterMicrosoftLogin(getAccessToken);
      }
    }
    if (nativeActive) {
      // Cookie-ONLY native probe (raw fetch, no bearer) so a Microsoft bearer can't make this
      // return 200 and mislabel an MS user as native.
      fetch('/api/auth/native/session', { credentials: 'include' })
        .then(r => (r.ok ? r.json() : null))   // non-200 (404 native-off, transient 5xx) → not native, don't throw on non-JSON
        .then(d => {
          if (d?.authenticated) {
            setNativeAuthed(true);
            setActiveAuthMethod('native');
            setNativeRemember(Boolean(d.session_remember));
          }
        })
        .catch(() => { /* not native */ })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);                      // Microsoft-only or no-auth: synchronous, no flash
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Schedule a proactive token refresh before the current token expires
  const scheduleRefresh = useCallback((idToken: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const exp = parseJwtExp(idToken);
    if (!exp) return;

    const expiresAt = exp * 1000; // ms
    const refreshAt = expiresAt - REFRESH_BUFFER_MS;
    const delay = refreshAt - Date.now();

    if (delay <= 0) {
      // Token already within refresh window — refresh immediately
      console.info('Token within refresh window, refreshing now');
      silentRefresh();
      return;
    }

    console.info(`Scheduling proactive token refresh in ${Math.round(delay / 1000)}s`);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      silentRefresh();
    }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msalInstance, config.enabled]);

  // Perform a silent token refresh (called proactively before expiry)
  const silentRefresh = useCallback(async () => {
    if (!msalInstance || !config.enabled) return;
    const account = msalInstance.getActiveAccount();
    if (!account) return;

    try {
      const result = await msalInstance.acquireTokenSilent({
        scopes: LOGIN_SCOPES,
        account,
        forceRefresh: true,
      });
      if (result.idToken) {
        console.info('Proactive token refresh succeeded');
        scheduleRefresh(result.idToken);
      }
    } catch (err) {
      console.warn('Proactive token refresh failed:', err);
      Sentry.captureException(err, { tags: { source: 'token_refresh' } });
      // Don't redirect here — the next API call will trigger the 401 flow
    }
  }, [msalInstance, config.enabled, scheduleRefresh]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const login = useCallback(async () => {
    if (!msalInstance) return;
    // Cancel any pending proactive refresh — it would race with the redirect.
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Fix-batch item 5 (Task-6 review): loginRedirect() navigates this tab
    // away to login.microsoftonline.com and back to a bare-origin
    // redirectUri, dropping any `?pushRedirect=` query string a push
    // notification's fresh window opened with. Stash the (validated)
    // target in sessionStorage now, while it's still in window.location —
    // useSwNavigationBridge consumes it once auth resolves and the Router
    // mounts. See swNavigationBridge.ts's doc comment for the full picture.
    stashPushRedirectForAuth(window.location.search);

    // MSAL keeps an "interaction in progress" flag in sessionStorage. When a
    // prior redirect got interrupted (most often after an overnight token
    // expiry), the flag sticks and loginRedirect throws interaction_in_progress
    // — the app sits silently stuck until the user fully closes the browser
    // (which is the only thing that clears sessionStorage). Ctrl+Shift+R
    // doesn't clear sessionStorage, which is why a hard reload didn't help.
    // Catch the stuck state, wipe MSAL's session keys, and retry.
    try {
      await msalInstance.loginRedirect({ scopes: LOGIN_SCOPES });
    } catch (err) {
      const code = (err as { errorCode?: string })?.errorCode;
      if (code === 'interaction_in_progress') {
        for (const key of Object.keys(sessionStorage)) {
          if (key.startsWith('msal.')) sessionStorage.removeItem(key);
        }
        try {
          await msalInstance.loginRedirect({ scopes: LOGIN_SCOPES });
          return;
        } catch (retryErr) {
          Sentry.captureException(retryErr, { tags: { source: 'login_retry' } });
        }
      } else {
        Sentry.captureException(err, { tags: { source: 'login' } });
      }
      throw err;
    }
  }, [msalInstance]);

  const logout = useCallback(async () => {
    // Gemini M (whole-branch review): a device that never unsubscribes its
    // push registration on logout keeps receiving urgent-task pushes for
    // whoever logs in NEXT on that same device/browser — the server-side
    // session is gone, but the browser subscription + DB row survive.
    // Best-effort and MUST run before the session is torn down below: the
    // backend unsubscribe call needs an authenticated session to resolve
    // "whose subscription is this", and a failure here (unsupported
    // platform, no subscription, network hiccup) must never block logout.
    try { await unsubscribePush(); } catch { /* best-effort — never blocks logout */ }

    if (activeAuthMethod === 'native') {
      try { await api.post('/auth/native/logout'); } catch { /* clear regardless */ }
      setNativeAuthed(false);
      setActiveAuthMethod(null);
      window.location.assign(window.location.origin);
      return;
    }
    if (activeAuthMethod === 'microsoft' && msalInstance) {
      if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
      // P3-A1 fix: a Microsoft sign-out must also revoke the bridge cookie
      // (session/bridge) — otherwise the 30-day bridge session + cookie
      // outlive the MSAL logout and the next person on a shared device
      // inherits this user's session. Best-effort: a failed revoke (e.g.
      // native auth disabled/network error) must never block the MS logout.
      try { await api.post('/auth/native/logout'); } catch { /* revoke best-effort */ }
      msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
    }
  }, [activeAuthMethod, msalInstance, unsubscribePush]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!msalInstance || !config.enabled) return null;

    const account = msalInstance.getActiveAccount();
    if (!account) return null;

    // MSAL already handles cache lookup, refresh-token redemption, iframe
    // fallback, and de-duplicates identical concurrent requests. Retrying a
    // timed-out iframe here only creates more orphaned authorization frames.
    // Do not redirect on failure; the 401 interceptor handles re-auth via
    // auth:unauthorized.
    try {
      const result = await msalInstance.acquireTokenSilent({
        scopes: LOGIN_SCOPES,
        account,
      });
      if (result.idToken) {
        scheduleRefresh(result.idToken);
      }
      return result.idToken;
    } catch (err) {
      console.warn('Silent token acquisition failed:', err);
      Sentry.captureException(err, {
        tags: { source: 'token_acquisition' },
      });
      return null;
    }
  }, [msalInstance, config.enabled, scheduleRefresh]);

  const markNativeAuthenticated = useCallback(() => {
    setNativeAuthed(true);
    setActiveAuthMethod('native');
  }, []);

  const markNativeUnauthenticated = useCallback(() => {
    setNativeAuthed(false);
    setActiveAuthMethod(null);
  }, []);

  // Compute isAuthenticated based on which auth method is active
  const isAuthenticated =
    (activeAuthMethod === 'microsoft' && !!user) ||
    (activeAuthMethod === 'native' && nativeAuthed === true) ||
    (!microsoftActive && !nativeActive);  // dev/CI bypass

  const authEnabled = microsoftActive || nativeActive;

  // idle_minutes is advertised by /auth/config; fall back to 60 if absent
  const idleMinutes: number = config.idle_minutes ?? 60;

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      authEnabled,
      user,
      login,
      logout,
      getAccessToken,
      authMethods,
      markNativeAuthenticated,
      markNativeUnauthenticated,
      activeAuthMethod,
      nativeRemember,
      idleMinutes,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
